import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';

import { mapPageTestIDs, myWeatherPageTestIDs, Theme } from '@constants';
import { useGetTileInventory, useSegmentTrackWeatherMap } from '@hooks';
import { navigationService } from '@services';

import {
  Map,
  MapHomePinSize,
  MapLegend,
  MapLegendSizeClass,
  OverlayType,
  ZoomPreset,
} from '../../../components/map/Map';
import { TWCTile } from '../../focusable/TWCTile/TWCTile';

const tPath = 'myWeatherPage.liveMap.';

const MapTileUI = ({ nextFocusDown, nextFocusLeft, nextFocusRight, nextFocusUp }) => {
  const { t } = useTranslation();

  const { error } = useGetTileInventory();

  const legendReference = useRef();
  const isFocused = useIsFocused();

  const { setSegmentTrackMap } = useSegmentTrackWeatherMap();

  const handleOnPress = () => {
    setSegmentTrackMap();
    navigationService.navigate(mapPageTestIDs.mapPage);
  };

  return (
    <TWCTile
      accessibilityHint={t(`${tPath}accessibilityHint`)}
      accessibilityLabel={error ? t(`${tPath}errorAccessibilityLabel`) : t(`${tPath}accessibilityLabel`)}
      accessibilityLiveRegion={'none'}
      height={522}
      nextFocusDown={nextFocusDown}
      nextFocusLeft={nextFocusLeft}
      nextFocusRight={nextFocusRight}
      nextFocusUp={nextFocusUp}
      testID={myWeatherPageTestIDs.liveMapTile}
      useLinearGradient={false}
      width={800}
      onPress={handleOnPress}
    >
      <View
        accessibilityLiveRegion={'none'}
        importantForAccessibility={'no-hide-descendants'}
        style={styles.tileContainer}
      >
        <>
          <Map.API
            animate={isFocused}
            attributionStyle={styles.mapAttribution}
            homePinSize={MapHomePinSize.small}
            overlayType={OverlayType.radar}
            style={styles.map}
            zoomPreset={ZoomPreset.local}
            onFrameChange={(mapInventoryLoading, frameTime, frameNumber, maxFrameNumber) => {
              if (legendReference?.current != null) {
                legendReference.current.updateLegendState(!mapInventoryLoading, frameNumber, maxFrameNumber, frameTime);
              }
            }}
          />
          <View style={styles.legendContainer}>
            <MapLegend
              animationLoading={false}
              overlayType={OverlayType.radar}
              ref={legendReference}
              sizeClass={MapLegendSizeClass.short}
            />
          </View>
        </>
      </View>
    </TWCTile>
  );
};

export const MapTile = {
  UI: MapTileUI,
};

const styles = StyleSheet.create({
  legendContainer: {
    bottom: Theme.px(50),
    flexDirection: 'row',
    position: 'absolute',
  },
  map: {
    flex: 1,
    zIndex: 0,
  },
  mapAttribution: {
    bottom: Theme.px(10),
    height: Theme.px(30),
    left: Theme.px(69),
    position: 'absolute',
    right: Theme.px(69),
  },
  tileContainer: {
    // apparently borderRadius alone doesn't always work for Native.
    borderBottomLeftRadius: Theme.tileBorderRadius,
    borderBottomRightRadius: Theme.tileBorderRadius,
    borderTopLeftRadius: Theme.tileBorderRadius,
    borderTopRightRadius: Theme.tileBorderRadius,
    // elevation: 500, // this was causing Android alert crawl to be really janky for some reason, disabling for now
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    overflow: 'hidden',
  },
});
